// What the panel says when the reader clicks a relationship.
//
// Clicking an edge in the Neo4j browser fills its panel with that edge; this
// is the same gesture answered the same way (product owner's reviewer, 6 Oct
// 2026). The explorer draws a tree rather than a free-floating graph, so a
// relationship is a node sitting between its object and the objects it
// reaches - which makes the chip the line, and clicking it the only gesture
// there is to make.
//
// It is deliberately not DetailsPanel with a third mode bolted on: that panel
// is three tabs about an *object* - its attributes, where it occurs, what it
// is related to - and a relationship has none of those. It has a name, a way
// round, and a list.
import React from "react";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { useViewConfig } from "../app/ViewConfigContext";
import TypeIcon from "../app/typeIcons";
import { humanRelationship, relationshipLabel, typeInfo } from "../../utils/viewConfig";
import { attributeRows, carriesNote, directionMark } from "./relationshipGroups";
import { distinguishers } from "./distinguish";

// Which way it runs, as a sentence naming the object the reader is standing
// on. "Out" and "in" are the graph's words, not a reader's.
const wayItRuns = (direction, here) =>
  direction === "in" ? `Points at ${here}` : `Runs out from ${here}`;

export default function RelationshipDetails({ relationship, direction, here, items, total = 0, carries = [], loading = false, onOpen, onClose }) {
  const config = useViewConfig();
  const label = relationshipLabel(config, relationship);
  // The name the graph carries, shown only where it is not simply the label
  // read back as words. An admin who renamed CONFLICTS_WITH to "Clashes with"
  // still has to find it in their own database; a relationship nobody renamed
  // would otherwise print its own name twice, once as words and once raw.
  const storedName = humanRelationship(relationship) === label ? "" : relationship;
  // `items` is null while the panel is still reading what this relationship
  // reaches - which is not the same answer as "it reaches nothing".
  const list = Array.isArray(items) ? items : [];
  const count = Math.max(total || 0, list.length);
  // What this relationship carries at all, named in the reader's words. Which
  // attributes those are is the admin's decision (Display -> Settings), so
  // nothing here knows one by name.
  const carriesLine = carriesNote(carries);
  // Where two of these are shown under the same name, what tells them apart.
  // No attribute is named here or there: it is whichever one differs.
  const apart = distinguishers(list);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Relationship
          </Typography>
          <Typography component="h2" sx={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.3, overflowWrap: "anywhere" }}>
            {label}
          </Typography>
          {storedName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontFamily: "monospace" }}>
              {storedName}
            </Typography>
          )}
        </Box>
        {onClose && (
          <IconButton size="small" onClick={onClose} aria-label="Close details">
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>

      <Stack spacing={0.5} sx={{ px: 2.5, pb: 1.5 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box aria-hidden="true" sx={{ fontSize: 16, lineHeight: 1, color: "text.secondary" }}>
            {directionMark(direction)}
          </Box>
          <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {wayItRuns(direction, here)}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {count} {count === 1 ? "object" : "objects"}
        </Typography>
        {/* What this relationship carries at all, on its own line: it is a
            fact about the relationship, where the values below are facts about
            one edge each. */}
        {carriesLine && (
          <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: "anywhere" }}>
            {carriesLine}
          </Typography>
        )}
      </Stack>

      <Divider />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 1.5, py: 1 }}>
        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ px: 1, py: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Reading what it reaches…
            </Typography>
          </Stack>
        ) : list.length ? (
          <Stack spacing={0.5}>
            {list.map((item) => {
              const info = typeInfo(config, item.type);
              return (
                <ButtonBase
                  key={item.guid || item.name}
                  onClick={() => onOpen && onOpen(item)}
                  title={`Show ${item.name} on the canvas`}
                  sx={{
                    justifyContent: "flex-start",
                    textAlign: "left",
                    width: "100%",
                    borderRadius: 1.5,
                    px: 1,
                    py: 0.75,
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <TypeIcon name={info.icon} sx={{ fontSize: 13, color: info.colour }} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {info.label}
                      </Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                      {item.name}
                    </Typography>
                    {/* The values differ per edge - that is the whole point of
                        recording how sure somebody is of one - so they belong
                        on the object, not in the heading. Nothing empty is
                        ever a row. */}
                    {apart.get(item.guid) && (
                      <Stack direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {apart.get(item.guid).label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                          {apart.get(item.guid).value}
                        </Typography>
                      </Stack>
                    )}
                    {attributeRows(item.attributes).map((row) => (
                      <Stack key={row.key} direction="row" spacing={0.75} alignItems="baseline" sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {row.label}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>
                          {row.value}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                </ButtonBase>
              );
            })}
          </Stack>
        ) : (
          // Known, and empty: every object at the other end is one this
          // reader's view hides. An empty list with no explanation reads as a
          // failure, so it says which it is.
          <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
            This relationship reaches nothing you can open.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
